import { useState, useEffect } from "react";
//#region
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { AccountingEnums, DimensionEnums, FormEnums } from "models/enums";
import { GenericFormSection } from "models/propTypes/genericFormProps";
import { AccountingFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
import { WintSuperkollAPIModel, WintSuperkollAPIModelFinancialReport } from "wint-api-client/dist/types";
import { FormikProps } from "formik";
//#endregion
import { DateHelper, FilterHelper, FilterQueryHelper, QueryStateHelper, DimensionHelper } from "utils/helpers";

// Presentational-only flag missing from the generated API filter types; persisted via the widened saved-filter
// payload in useFinancialReportService's `*ReportToAPIFilter` formatters, and read back here.
type FinancialReportUIFlags = Pick<AccountingFormTypes.FinancialReportFilter, "expandAllRows">;

// Balance Sheet (balanceReport/report.tsx) reuses this hook with no average-month/difference column to hide.
function useFinancialReportFilterDefinition(
  type: AccountingEnums.FinancialReportType,
  supportsHideAverageMonthAndDifference: boolean = true
) {
  const dimensionTypes = useSelector((store: AppState) => store.common.dimensions.allDimensionTypes);
  const [primaryDimensionId, setPrimaryDimensionId] = useState<string>();
  const [secondDimensionTypes, setSecondDimensionTypes] = useState<WintSuperkollAPIModel.DimensionType[]>();

  const apiToReportFilterFormatter = async (
    filter: WintSuperkollAPIModelFinancialReport.FinancialReportFilter & Partial<FinancialReportUIFlags>
  ): Promise<AccountingFormTypes.FinancialReportFilter> => {
    const reportFilter: AccountingFormTypes.FinancialReportFilter = {
      dimensions: filter.dimensions,
      showAccountNumber: filter.showAccountNumber || undefined,
      hideAverageMonthAndDifference: filter.hideAverageMonthAndDifference || undefined,
      expandAllRows: filter.expandAllRows || undefined,
    };

    if (filter.columns?.length) {
      FilterHelper.setDateRange(
        reportFilter,
        FormFieldConstants.Accounting.FinancialReportFilter.MonthRange,
        filter.columns[0].startMonth ? DateHelper.getDateFromAPIYearMonth(filter.columns[0].startMonth) : undefined,
        filter.columns[0].endMonth ? DateHelper.getDateFromAPIYearMonth(filter.columns[0].endMonth) : undefined
      );

      // Filters saved before comparison columns were persisted only hold the primary column, so a single
      // column means "no preference" and leaves the on-screen comparison columns alone.
      if (filter.columns.length > 1) {
        reportFilter.comparisonColumns = filter.columns.slice(1).map((column) => ({
          start: column.startMonth ? DateHelper.getDateFromAPIYearMonth(column.startMonth) : undefined,
          end: column.endMonth ? DateHelper.getDateFromAPIYearMonth(column.endMonth) : undefined,
        }));
      }
    }

    return reportFilter;
  };

  const apiToReportMonthlyFilterFormatter = async (
    filter: WintSuperkollAPIModelFinancialReport.FinancialReportMonthlyFilter & Partial<FinancialReportUIFlags>
  ): Promise<AccountingFormTypes.FinancialReportFilter> => {
    const reportFilter: AccountingFormTypes.FinancialReportFilter = {
      dimensions: filter.dimensions,
      showAccountNumber: filter.showAccountNumber || undefined,
      hideAverageMonthAndDifference: filter.hideAverageMonthAndDifference || undefined,
      expandAllRows: filter.expandAllRows || undefined,
    };

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.Accounting.FinancialReportFilter.MonthRange,
      filter.startMonth ? DateHelper.getDateFromAPIYearMonth(filter.startMonth) : undefined,
      filter.endMonth ? DateHelper.getDateFromAPIYearMonth(filter.endMonth) : undefined
    );

    return reportFilter;
  };

  const apiToReportDimensionsFilterFormatter = async (
    filter: WintSuperkollAPIModelFinancialReport.FinancialReportMultiDimensionFilter & Partial<FinancialReportUIFlags>
  ): Promise<AccountingFormTypes.FinancialReportFilter> => {
    const reportFilter: AccountingFormTypes.FinancialReportFilter = {};

    if (filter.dimensionType) {
      reportFilter.dimensionTypeId = filter.dimensionType;
      const dimensionType = dimensionTypes?.find((d) => d.id === filter.dimensionType);
      if (dimensionType)
        reportFilter.dimensionType = DimensionHelper.getDimensionTypeText(dimensionType.type, dimensionType.name);
    }

    if (filter.crossDimensionTypes) {
      reportFilter.secondDimensionTypes = filter.crossDimensionTypes.map((d) => ({
        id: d,
        value: dimensionTypes?.find((d) => d.id === filter.dimensionType)?.name ?? "",
      }));
    }

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.Accounting.FinancialReportFilter.MonthRange,
      filter.startMonth ? DateHelper.getDateFromAPIYearMonth(filter.startMonth) : undefined,
      filter.endMonth ? DateHelper.getDateFromAPIYearMonth(filter.endMonth) : undefined
    );

    reportFilter.onlyActiveDimensions = filter.onlyActiveDimensions ?? false;
    reportFilter.onlyDimensionsWithTransactions = filter.onlyDimensionsWithTransactions ?? false;
    reportFilter.showAccountNumber = filter.showAccountNumber || undefined;
    reportFilter.hideAverageMonthAndDifference = filter.hideAverageMonthAndDifference || undefined;
    reportFilter.expandAllRows = filter.expandAllRows || undefined;

    return reportFilter;
  };

  useEffect(() => {
    setSecondDimensionTypes(dimensionTypes?.filter((d) => d.id !== primaryDimensionId) ?? []);
  }, [primaryDimensionId, dimensionTypes]);

  const onPrimaryDimensionChangeHandler = (
    name: string,
    updatedValue: any,
    formikProps: FormikProps<AccountingFormTypes.FinancialReportFilter>
  ) => {
    const primaryDimensionId = formikProps.values.dimensionTypeId;
    setPrimaryDimensionId(primaryDimensionId);

    formikProps.setFieldValue(
      "secondDimensionTypes",
      formikProps.values.secondDimensionTypes?.filter((d) => d.id !== primaryDimensionId)
    );
  };

  const filterFormFields: GenericFormSection<AccountingFormTypes.FinancialReportFilter>[] = [
    {
      label: TranslationKeys.Common.Dates,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        monthRange: {
          label: TranslationKeys.Common.MonthPascal,
          type: FormEnums.FormFieldType.MONTHRANGE,
        },
      },
    },
    {
      label: TranslationKeys.Common.Tags,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        dimensions: {
          label: TranslationKeys.Common.Tags,
          type: FormEnums.FormFieldType.DIMENSIONMULTIPLEINPUTSELECTOR,
          hidden: type === AccountingEnums.FinancialReportType.Tag,
          extraProps: {
            type: DimensionEnums.DimensionCategory.All,
          },
        },
        dimensionType: {
          label: TranslationKeys.Common.TagsX,
          descriptionText: TranslationKeys.Accounting.TagsXDescription,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          hidden: type !== AccountingEnums.FinancialReportType.Tag,
          extraProps: {
            referenceFieldName: FormFieldConstants.Accounting.FinancialReportFilter.DimensionTypeId,
            items: dimensionTypes?.map((d) => ({
              id: d.id ?? "",
              value: DimensionHelper.getDimensionTypeText(d.type, d.name),
            })),
          },
          onChangeHandler: onPrimaryDimensionChangeHandler,
          enableOnChangeAlways: true,
        },
        secondDimensionTypes: {
          label: TranslationKeys.Common.TagsY,
          descriptionText: TranslationKeys.Accounting.TagsYDescription,
          type: FormEnums.FormFieldType.MULTIPLEAUTOCOMPLETE,
          hidden: type !== AccountingEnums.FinancialReportType.Tag,
          extraProps: {
            items: secondDimensionTypes?.map((d) => ({
              id: d.id ?? "",
              value: DimensionHelper.getDimensionTypeText(d.type, d.name),
            })),
          },
          showNotSelectedTextWhenReadOnly: true,
        },
        onlyDimensionsWithTransactions: {
          label: TranslationKeys.Accounting.OnlyDimensionsWithTransactions,
          type: FormEnums.FormFieldType.TOGGLE,
          hidden: type !== AccountingEnums.FinancialReportType.Tag,
        },
        onlyActiveDimensions: {
          label: TranslationKeys.Accounting.OnlyActiveDimensions,
          type: FormEnums.FormFieldType.TOGGLE,
          hidden: type !== AccountingEnums.FinancialReportType.Tag,
        },
        showAccountNumber: {
          label: TranslationKeys.Accounting.ShowAccount,
          type: FormEnums.FormFieldType.TOGGLE,
        },
        hideAverageMonthAndDifference: {
          label: TranslationKeys.Accounting.HideAverageMonthAndDifference,
          type: FormEnums.FormFieldType.TOGGLE,
          hidden: !supportsHideAverageMonthAndDifference || type !== AccountingEnums.FinancialReportType.Standard,
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<AccountingFormTypes.FinancialReportFilter> = {
    monthRange: QueryStateHelper.monthRangeQueryEncoder(FormFieldConstants.Accounting.FinancialReportFilter.MonthRange),
    comparisonColumns: QueryStateHelper.monthRangeListQueryEncoder(
      FormFieldConstants.Accounting.FinancialReportFilter.ComparisonColumns
    ),
    dimensions: QueryStateHelper.dimensionsQueryEncoder(FormFieldConstants.Accounting.FinancialReportFilter.Dimensions),
    dimensionTypeId: QueryStateHelper.stringQueryEncoder(
      FormFieldConstants.Accounting.FinancialReportFilter.DimensionTypeId
    ),
    dimensionType: QueryStateHelper.stringQueryEncoder(
      FormFieldConstants.Accounting.FinancialReportFilter.DimensionType
    ),
    secondDimensionTypes: QueryStateHelper.multipleAutoCompleteItemsQueryEncoder(
      FormFieldConstants.Accounting.FinancialReportFilter.SecondDimensionTypes
    ),
    onlyDimensionsWithTransactions: QueryStateHelper.booleanQueryEncoder(
      FormFieldConstants.Accounting.FinancialReportFilter.OnlyDimensionsWithTransactions
    ),
    onlyActiveDimensions: QueryStateHelper.booleanQueryEncoder(
      FormFieldConstants.Accounting.FinancialReportFilter.OnlyActiveDimensions
    ),
    showAccountNumber: QueryStateHelper.booleanQueryEncoder(
      FormFieldConstants.Accounting.FinancialReportFilter.ShowAccountNumber
    ),
    expandAllRows: FilterQueryHelper.presentational(
      QueryStateHelper.booleanQueryEncoder(FormFieldConstants.Accounting.FinancialReportFilter.ExpandAllRows)
    ),
    ...(supportsHideAverageMonthAndDifference
      ? {
          hideAverageMonthAndDifference: QueryStateHelper.booleanQueryEncoder(
            FormFieldConstants.Accounting.FinancialReportFilter.HideAverageMonthAndDifference
          ),
        }
      : {}),
  };

  return {
    filterFormFields,
    filterQueryStateHandler,
    apiToReportFilterFormatter,
    apiToReportMonthlyFilterFormatter,
    apiToReportDimensionsFilterFormatter,
  };
}

export default useFinancialReportFilterDefinition;
